import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Player } from '@lordicon/react';
import Layout from '../../Layout/Layout';
import TitleBar from '../../Layout/TitleBar';
import { Form, Formik } from 'formik';
import {
  Container,
  Row,
  Col,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import validationSchema from './validationSchema';
import {
  useTranslator,
  FormikInput as Input,
  useAppContext,
  formatIsoDate,
  formatIsoDateTime,
  useToast,
  withLoading,
  callNativeMobileApp,
  withMobileAppMode,
} from '@front-activerse/common-components';
import UniversalPlaceholder from '../../Layout/UniversalPlaceholder';
import { useParams, useNavigate } from 'react-router-dom';
import AmountWithUnitInput from './AmountWithUnitInput';
import TrackerFeedback from './TrackerFeedback';

interface AddActivityContainerProps {
  // Add mode
  addManualActivity?: (input: any) => Promise<any>;
  activityData?: any;
  // Edit mode
  editManualActivity?: (input: any) => Promise<any>;
  removeActivity?: (activityId: number) => Promise<any>;
  activityId?: number;
  mobileAppMode?: boolean;
}

const AddActivityContainer: React.FC<AddActivityContainerProps> = ({
  addManualActivity,
  activityData,
  editManualActivity,
  removeActivity,
  activityId,
  mobileAppMode,
}) => {
  const { t } = useTranslator();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [showActivityNameEdit, setShowActivityNameEdit] = useState(false);
  const navigate = useNavigate();

  // Determine mode: edit mode if editManualActivity is provided
  const isEditMode = Boolean(editManualActivity);

  const {
    images: { aiBadgeTiny },
    lotties: { infoIcon, trashIcon, editIcon },
  } = useAppContext();

  const playerRefInfo = useRef<Player>(null);
  const playerRefTrash = useRef<Player>(null);
  const playerRefEdit = useRef<Player>(null);
  const iconsIconPrimaryColor = useMemo(() => {
    if (typeof document === 'undefined') return '#000000';
    const color = getComputedStyle(document.documentElement)
      .getPropertyValue('--icons-icon-primary-color')
      .trim();
    return color || '#000000';
  }, []);
  const iconsIconMiddleColor = useMemo(() => {
    if (typeof document === 'undefined') return '#000000';
    const color = getComputedStyle(document.documentElement)
      .getPropertyValue('--icons-icon-middle-color')
      .trim();
    return color || '#000000';
  }, []);

  const iconsIconErrorColor = useMemo(() => {
    if (typeof document === 'undefined') return '#000000';
    const color = getComputedStyle(document.documentElement)
      .getPropertyValue('--icons-icon-error-color')
      .trim();
    return color || '#000000';
  }, []);

  const imageUrl = activityData?.imageUrl || '';

  // Helper to extract date and time from ISO datetime string
  const extractDateAndTime = (isoDateTime: string) => {
    if (!isoDateTime) return { date: formatIsoDate(new Date()), time: getCurrentTime() };
    const dateObj = new Date(isoDateTime);
    const date = formatIsoDate(dateObj);
    const hours = dateObj.getHours().toString().padStart(2, '0');
    const minutes = dateObj.getMinutes().toString().padStart(2, '0');
    const time = `${hours}:${minutes}`;
    return { date, time };
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const minDateObj = new Date(today);
  minDateObj.setDate(minDateObj.getDate() - 30);
  const minDate = formatIsoDate(minDateObj);

  const maxDateObj = new Date(today);
  maxDateObj.setDate(maxDateObj.getDate() + 30);
  const maxDate = formatIsoDate(maxDateObj);

  const { date } = useParams();
  const [showDateInput, setShowDateInput] = useState(
    date && date !== formatIsoDate(new Date()) ? true : false
  );

  const { toastError, toastSuccess } = useToast();

  // Distance conversion rates (to meters)
  const DISTANCE_TO_METERS: Record<string, number> = {
    m: 1,
    km: 1000,
    mi: 1609.34,
    yd: 0.9144,
  };

  // Convert distance from source unit to target unit
  const convertDistance = (amount: number, sourceUnit: string, targetUnit: string): number => {
    if (sourceUnit === targetUnit || amount === 0) {
      return amount;
    }

    // Convert to meters first
    const amountInMeters = amount * DISTANCE_TO_METERS[sourceUnit];
    // Convert from meters to target unit
    const convertedAmount = amountInMeters / DISTANCE_TO_METERS[targetUnit];

    // Round by target unit: m, yd = integers; km = 2 decimals; mi = 1 decimal
    if (targetUnit === 'km') {
      return Math.round(convertedAmount * 100) / 100;
    }
    if (targetUnit === 'mi') {
      return Math.round(convertedAmount * 10) / 10;
    }
    return Math.round(convertedAmount); // m, yd
  };

  const getCurrentTime = () => {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const inputRef = useRef<HTMLInputElement>(null);
  const activityNameInputRef = useRef<HTMLInputElement>(null);

  const fakeSubmit = () => {
    // blur input
    inputRef.current?.blur();
  };

  // Focus activity name input when edit mode is activated
  useEffect(() => {
    if (showActivityNameEdit) {
      const timer = setTimeout(() => {
        activityNameInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showActivityNameEdit]);

  const handleDateInputBlur = (value: string | number) => {
    // If date is still today's date, hide the input and show "Today" div
    if (value === formatIsoDate(new Date())) {
      setShowDateInput(false);
    } else {
      setShowDateInput(true);
    }
  };

  const renderActivityImage = () => {
    return <img src={imageUrl} className="img-fluid rounded mb-3 mb-md-0" alt="" />;
  };

  // Calculate initial values based on mode
  const getInitialValues = () => {
    if (isEditMode && activityData) {
      // Edit mode: use fetched activity data

      console.log('activityData', activityData);

      const { date, time } = extractDateAndTime(activityData.loggedAt);
      return {
        date: date || formatIsoDate(new Date()),
        time: time || getCurrentTime(),
        burnedCalories: activityData.caloriesBurned || 0,
        duration: activityData.durationSeconds ? Math.round(activityData.durationSeconds / 60) : 0,
        distance: activityData.distance ?? null,
        distanceUnit: activityData.distanceUnit || 'km',
        activityName: activityData.name || '',
      };
    } else {
      // Add mode: use activityData from location.state
      return {
        date: date ? date : formatIsoDate(new Date()),
        time: getCurrentTime(),
        burnedCalories: activityData?.totalKcals || 0,
        duration: activityData?.durationMinutes || 0,
        distance: activityData?.distanceValue ?? null,
        distanceUnit: activityData?.distanceUnit || 'km',
        activityName: activityData?.activityName || '',
      };
    }
  };

  const initialValues = getInitialValues();

  const handleSubmit = async (values: typeof initialValues) => {
    if (!activityData) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (isEditMode && editManualActivity && activityId) {
        // Edit mode
        const activityInput: Record<string, unknown> = {
          activityId: activityId,
          dateTime: formatIsoDateTime(values.date, `${values.time}:00`),
          name: values.activityName,
          caloriesBurned: +values.burnedCalories,
          durationSeconds: +values.duration * 60,
        };

        // Only include distance if it's provided
        if (values.distance !== null && values.distance !== undefined) {
          activityInput.distance = parseFloat(values.distance as string);
          activityInput.distanceUnit = values.distanceUnit;
        }

        const response = await editManualActivity(activityInput);

        if (response.__typename === 'DailyTrackerEditManualActivitySuccess') {
          // refresh native tracker data
          callNativeMobileApp({
            action: 'refresh_data',
            payload: {
              module: 'tracker',
              date: values.date,
            },
          });

          // refresh native tracker data
          callNativeMobileApp({
            action: 'refresh_data',
            payload: {
              module: 'tracker',
              date: date,
            },
          });

          toastSuccess(<p className="mb-0 w-100">{t('tracker.editActivityConfirmation.toast')}</p>);
          navigate(-1);
        } else {
          toastError();
        }
      } else if (addManualActivity) {
        // Add mode
        const activityInput: Record<string, unknown> = {
          dateTime: formatIsoDateTime(values.date, `${values.time}:00`),
          name: values.activityName,
          activityType: activityData.activityType,
          caloriesBurned: +values.burnedCalories,
          durationSeconds: +values.duration * 60,
          sourceType: 'ai',
        };

        // Only include distance if it's provided
        if (values.distance !== null && values.distance !== undefined) {
          activityInput.distance = parseFloat(values.distance as string);
          activityInput.distanceUnit = values.distanceUnit;
        }

        const response = await addManualActivity(activityInput);

        if (response.__typename === 'DailyTrackerAddManualActivitySuccess') {
          // refresh native tracker data
          callNativeMobileApp({
            action: 'refresh_data',
            payload: {
              module: 'tracker',
              date: values.date,
            },
          });
          callNativeMobileApp({
            action: 'show_toast',
            payload: {
              type: 'success',
              content: `${t('tracker.activityLoggedConfirmation.toast')}`,
            },
          });
          callNativeMobileApp({
            action: 'close_modal',
          });
          if (!mobileAppMode) {
            toastSuccess(
              <p className="mb-0 w-100">{t('tracker.activityLoggedConfirmation.toast')}</p>
            );
            navigate(-2);
          }
        } else {
          toastError();
        }
      }
    } catch (error) {
      toastError();
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleAiModal = () => {
    setShowAiModal(!showAiModal);
  };

  const toggleModal = () => {
    setShowAlertModal(!showAlertModal);
  };

  const removeActivityHandler = async () => {
    setShowAlertModal(false);
    if (isEditMode && removeActivity && activityId) {
      // Edit mode: call remove mutation
      setIsRemoving(true);
      try {
        const response = await removeActivity(activityId);
        if (response.__typename === 'BasicMutationSuccess') {
          navigate(-1);
        } else {
          toastError();
        }
      } catch (error) {
        toastError();
      } finally {
        setIsRemoving(false);
      }
    }
  };

  return (
    <>
      <Layout>
        <TitleBar showBackBtn={true}>{t('tracker.logMeal.navbarTitle')}</TitleBar>

        <Formik
          onSubmit={fakeSubmit}
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize={isEditMode}
          validateOnMount={true}
        >
          {({ isValid, values, setFieldValue, setValues, dirty }) => {
            const handleDistanceUnitChange = (newUnit: string) => {
              const currentDistance = parseFloat(values.distance as string) || 0;
              const currentUnit = values.distanceUnit as string;

              if (currentDistance > 0 && currentUnit !== newUnit) {
                const convertedDistance = convertDistance(currentDistance, currentUnit, newUnit);
                setValues({
                  ...values,
                  distance: convertedDistance,
                  distanceUnit: newUnit,
                });
              } else {
                setFieldValue('distanceUnit', newUnit);
              }
            };

            return (
              <Form>
                <div className="page-template-tracker">
                  <Container className="py-md-4 full-height2">
                    <Row>
                      <Col xl={{ size: 10, offset: 1 }}>
                        <div className="page-container">
                          <div className="tracker-container">
                            <Row>
                              <Col md="6">{renderActivityImage()}</Col>
                              <Col md="6">
                                <div className="d-flex">
                                  <div className="me-1 position-relative">
                                    <Input
                                      name="date"
                                      type="date"
                                      min={minDate}
                                      max={maxDate}
                                      translateErrMsg
                                      callbackOnChange={handleDateInputBlur}
                                    />
                                    <div className={!showDateInput ? 'today' : 'd-none'}>
                                      {t('time.day.today')}
                                    </div>
                                  </div>
                                  <div className="me-1">
                                    <Input name="time" type="time" translateErrMsg />
                                  </div>
                                </div>

                                <div className="d-flex justify-content-between align-items-start mt-xxs">
                                  {showActivityNameEdit ? (
                                    <div className="w-100 text-end">
                                      <Button
                                        color="link"
                                        size="sm"
                                        className="p-0 mb-xxs"
                                        onClick={() => setShowActivityNameEdit(false)}
                                      >
                                        {t('mealDetails.notesBox.confirmButton')}
                                      </Button>
                                      <Input
                                        name="activityName"
                                        type="text"
                                        className="w-100"
                                        innerRef={activityNameInputRef}
                                      />
                                    </div>
                                  ) : (
                                    <>
                                      <h2 className="title-large semi-bold mb-0">
                                        {values.activityName}
                                      </h2>
                                      <Button
                                        color="link"
                                        size="sm"
                                        className="p-0 ms-2 mt-2"
                                        onClick={() => setShowActivityNameEdit(true)}
                                        onMouseEnter={() =>
                                          playerRefEdit.current?.playFromBeginning()
                                        }
                                        onTouchStart={() =>
                                          playerRefEdit.current?.playFromBeginning()
                                        }
                                      >
                                        <Player
                                          ref={playerRefEdit}
                                          icon={editIcon}
                                          size={20}
                                          colorize={iconsIconPrimaryColor}
                                        />
                                      </Button>
                                    </>
                                  )}
                                  {!showActivityNameEdit && (
                                    <Input name="activityName" type="hidden" />
                                  )}
                                </div>
                              </Col>
                            </Row>
                          </div>
                        </div>

                        <div className="tracker-ingredients-list">
                          <div className="d-flex justify-content-between align-items-center mb-l">
                            <p className="text-text-main-color semi-bold mb-0">
                              {t('tracker.activityDetails.title')}
                            </p>
                            <div className="d-flex align-items-center">
                              <img
                                src={aiBadgeTiny}
                                alt=""
                                className="me-s"
                                style={{ width: '39px' }}
                              />
                              <p className="mb-0 description text-text-variant-color">
                                {t('tracker.aiSignature')}
                              </p>
                              <Button
                                color="link"
                                type="button"
                                className="p-0 ms-s border-0 bg-transparent"
                                onClick={toggleAiModal}
                                aria-label={t('tracker.aiSignature.popup.title')}
                                onMouseEnter={() => playerRefInfo.current?.playFromBeginning()}
                                onTouchStart={() => playerRefInfo.current?.playFromBeginning()}
                              >
                                <Player
                                  ref={playerRefInfo}
                                  icon={infoIcon}
                                  size={20}
                                  colorize={iconsIconMiddleColor}
                                />
                              </Button>
                            </div>
                          </div>

                          <ul className="tracker-ingredients-list-items">
                            <li className="tracker-ingredient-item pb-0">
                              <div className="d-flex justify-content-between align-items-center">
                                <span className="small text-text-main-color semi-bold mb-m">
                                  {t('tracker.activityDetails.caloriesBurned')}
                                </span>
                                <Input
                                  name="burnedCalories"
                                  className="activity-input"
                                  type="number"
                                  unit={'kcal'}
                                  translateErrMsg={true}
                                  step={1}
                                  disableMetaTouched
                                />
                              </div>
                            </li>
                            <li className="tracker-ingredient-item pb-0">
                              <div className="d-flex justify-content-between align-items-center">
                                <span className="small text-text-main-color semi-bold mb-m">
                                  {t('tracker.activityDetails.duration')}
                                </span>
                                <Input
                                  name="duration"
                                  className="activity-input"
                                  type="number"
                                  unit={'min'}
                                  translateErrMsg={true}
                                  step={1}
                                  disableMetaTouched
                                />
                              </div>
                            </li>

                            {values.distance !== null && values.distance !== undefined && (
                              <li className="tracker-ingredient-item">
                                <div className="d-flex justify-content-between align-items-center">
                                  <span className="small text-text-main-color semi-bold">
                                    {t('tracker.activityDetails.distance')}
                                  </span>
                                  <AmountWithUnitInput
                                    amountName="distance"
                                    unitName="distanceUnit"
                                    availableUnits={['m', 'km', 'mi', 'yd']}
                                    currentUnit={values.distanceUnit as string}
                                    onUnitChange={handleDistanceUnitChange}
                                    step={
                                      values.distanceUnit === 'km'
                                        ? 0.01
                                        : values.distanceUnit === 'mi'
                                        ? 0.1
                                        : 1
                                    }
                                    translateErrMsg={true}
                                    disableMetaTouched
                                  />
                                </div>
                              </li>
                            )}
                          </ul>

                          {activityData?.requestId && (
                            <TrackerFeedback requestId={activityData.requestId} />
                          )}

                          <div className="text-center mt-xl">
                            <Button
                              color="link"
                              className="w-100 mb-xl d-flex align-items-center justify-content-center"
                              onClick={() => {
                                if (isEditMode) {
                                  setShowAlertModal(true);
                                } else {
                                  if (mobileAppMode) {
                                    setTimeout(() => {
                                      callNativeMobileApp({
                                        action: 'close_modal',
                                      });
                                    }, 0);
                                  } else {
                                    navigate(-Number(sessionStorage.getItem('tracker_depth')));
                                  }
                                }
                              }}
                              disabled={isRemoving}
                              onMouseEnter={() => playerRefTrash.current?.playFromBeginning()}
                              onTouchStart={() => playerRefTrash.current?.playFromBeginning()}
                            >
                              <>
                                <div className="me-1 d-inline-block">
                                  <Player
                                    ref={playerRefTrash}
                                    icon={trashIcon}
                                    size={20}
                                    colorize={iconsIconErrorColor}
                                  />
                                </div>
                                <span className="text-text-error-color">
                                  {t('yourActivity.removeButton')}
                                </span>
                              </>
                            </Button>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </Container>

                  {(!isEditMode || (isEditMode && dirty)) && (
                    <div className="sticky-footer">
                      <Container className="px-3">
                        <Row>
                          <Col xs="12" md={{ size: 4, offset: 4 }}>
                            <Button
                              type="button"
                              onClick={() => handleSubmit(values)}
                              color="primary"
                              className="w-100"
                              disabled={isSubmitting || !isValid}
                            >
                              {isSubmitting ? (
                                <>
                                  <Spinner />
                                  <span className="ms-2">{t('data-processing')}</span>
                                </>
                              ) : isEditMode ? (
                                t('tracker.saveButton')
                              ) : (
                                t('tracker.activity.logButton')
                              )}
                            </Button>
                          </Col>
                        </Row>
                      </Container>
                    </div>
                  )}
                </div>
              </Form>
            );
          }}
        </Formik>
      </Layout>
      <Modal isOpen={showAiModal} toggle={toggleAiModal} centered size="md">
        <ModalHeader toggle={toggleAiModal} />
        <ModalBody className="pt-0">
          <h2 className="title-medium semi-bold">{t('tracker.aiSignature.popup.title')}</h2>
          <p>{t('tracker.aiSignature.popup.activity.description')}</p>

          <Button color="primary" className="w-100" onClick={toggleAiModal}>
            {t('tracker.aiSignature.popup.button')}
          </Button>
        </ModalBody>
      </Modal>

      <Modal isOpen={showAlertModal} toggle={toggleModal} centered size="md">
        <ModalBody>
          <h4 className="title-medium semi-bold mb-l mt-s">
            {t('tracker.history.removePopup.syncedActivity.title')}
          </h4>

          <p className="mb-0 text-text-variant-color">
            {t('tracker.history.removePopup.syncedActivity.description')}
          </p>
        </ModalBody>
        <ModalFooter>
          <Row className="w-100 m-0">
            <Col xs="6" className="ps-0 pe-2">
              <Button color="secondary" outline className="w-100" onClick={() => toggleModal()}>
                {t('tracker.history.removePopup.cancelButton')}
              </Button>
            </Col>
            <Col xs="6" className="ps-2 pe-0">
              <Button
                color="primary"
                className="w-100"
                onClick={() => removeActivityHandler()}
                disabled={!showAlertModal}
              >
                {t('tracker.history.removePopup.removeButton')}
              </Button>
            </Col>
          </Row>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default withLoading(withMobileAppMode(AddActivityContainer), UniversalPlaceholder);
